/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import DropdownMenu from 'dropdown-menu'
import { getValue } from 'remote-config-value'
import { getFeatureEnabled } from 'feature-flag'
import { makeStyles } from '@material-ui/core/styles'

import StatusChip from '../../shared/StatusChip'
import BrandsLandingPage from '../LandingPage'
import { Table } from 'foundations-library/components'
import ListContainer from '../../shared/ListContainer'
import DropdownButton from '../../shared/DropdownButton'
import BrandsContext from '../../../contexts/BrandsContext'
import ConfirmDeleteModal from '../../shared/ConfirmDeleteModal'
import { EnrollmentType } from '../../../interface/EnrollmentType'

const styles = makeStyles(() => {
    return {
        deleteMenuItem: {
            color: '#F5472C !important'
        }
    }
})

type MenuItem = {
    text: string
    onClick: () => void
    disabled?: boolean
    tooltipText?: string | null
    color?: string
    className?: string
    'data-testid': string
}

interface Props {
    onClickPreview(v): unknown
    onClickAddNew(): unknown
}
/**
 *
 */
const Enrollment = ({
    onClickPreview,
    onClickAddNew
}: Props) => {
    const [deleteBrandFeature, setDeleteBrandFeature] = useState(false)
    const [editBrandFeature, setEditBrandFeature] = useState(false)

    const classes = styles()

    const {
        brands,
        setBrandNetworkError,
        brandNetworkError,
        deleteBrand,
        enrollmentEnabled,
        networkPending
    } = useContext(BrandsContext)

    useEffect(() => {
        getFeatureEnabled('ten_dlc_delete_brand_enabled').then(setDeleteBrandFeature)
        getFeatureEnabled('ten_dlc_edit_brand_enabled').then(setEditBrandFeature)
        setBrandNetworkError(null)
    }, [])

    const includeEditInMenu = (row) => {
        return row.registration_status !== '' && !['pending-review', 'submitted', 'processing'].includes(row.registration_status)
    }

    const [deleting, setDeleting] = useState(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const deleteReachedNetworkPending = useRef(false)

    const renderRegisteredOnColumn = (row) => {
        const createdDate = row.created_date
        const d = new Date(0)
        d.setUTCSeconds(createdDate)

        const year = d.toLocaleString('default', { year: 'numeric' })
        const month = d.toLocaleString('default', { month: '2-digit' })
        const day = d.toLocaleString('default', { day: '2-digit' })

        return `${year}-${month}-${day}`
    }
    const getDeleteBrandItem = (row): MenuItem | null => {
        if (!deleteBrandFeature) return null

        const hasCampaigns = row.campaign_count > 0
        const isDeleted = row.deleted_at != null
        const disableDelete = hasCampaigns || isDeleted

        const tooltipText = disableDelete
            ? hasCampaigns
                ? getValue('sms_enrollment_brand_delete_tooltip')
                : isDeleted
                    ? getValue('sms_enrollment_enrollment_status_deleted_tooltip')
                    : null
            : null
        return {
            text: 'Delete',
            onClick: () => setDeleting(row),
            disabled: disableDelete,
            tooltipText: tooltipText,
            'data-testid': `item-delete-${row.csp_brand_id}`
        }
    }

    const brandEditDisabledHelper = (row) => {
        const isDeleted = row.deleted_at != null
        const disabled = isDeleted || !enrollmentEnabled

        return {
            editDisabled: disabled,
            editTooltip:
                disabled
                    ? !enrollmentEnabled
                        ? getValue('sms_enrollment_feature_disabled_tooltip')
                        : isDeleted
                            ? getValue('sms_enrollment_enrollment_status_deleted_tooltip')
                            : null
                    : null
        }
    }

    const renderOptionsColumn = (row) => {
        const menuItems: MenuItem[] = []

        const testUniqueIdentifier = row.csp_campaign_id || row.csp_brand_id || row.voip_did_id

        if (editBrandFeature || includeEditInMenu(row)) {
            const { editDisabled, editTooltip } = brandEditDisabledHelper(row)

            menuItems.push({
                text: 'Review/Edit',
                onClick: () => onClickPreview(row.csp_brand_id),
                disabled: editDisabled,
                tooltipText: editDisabled ? editTooltip : null,
                'data-testid': `item-preview-${testUniqueIdentifier}`
            })
        }

        const deleteMenuItem = getDeleteBrandItem(row)

        if (deleteMenuItem) {
            menuItems.push({
                ...deleteMenuItem,
                text: 'Delete',
                className: classes.deleteMenuItem,
                'data-testid': `item-delete-${testUniqueIdentifier}`
            })
        }

        return menuItems.length > 0
            ? (
                <DropdownMenu
                    button={DropdownButton}
                    menuItems={menuItems}
                    dropdownStyle='configure'
                    data-testid={`item-drop-down-${testUniqueIdentifier}`}
                />
            )
            : <></>
    }

    const rows = useMemo(
        () => {
            return brands?.map((row) => ([
                row.csp_brand_id,
                row.name,
                renderRegisteredOnColumn(row),
                <StatusChip
                    latestEvent={row.latest_event}
                    status={row.registration_status}
                    enrollmentType={EnrollmentType.Brand}
                    deleted={row.deleted_at != null}
                    itemsAttached={row.campaign_count}
                    key={row.csp_brand_id}
                    row-testid={`chip-${row.csp_brand_id}`}
                />,
                renderOptionsColumn(row)
            ])) || []
        },
        [brands, deleteBrandFeature, editBrandFeature]
    )

    const onDeleteHandler = async () => {
        if (deleting == null || isDeleting) return
        setBrandNetworkError(null)
        deleteReachedNetworkPending.current = false
        setIsDeleting(true)
        try {
            await deleteBrand({
                csp_brand_id: deleting.csp_brand_id
            })
        } catch (err) {
            console.error('Error while deleting the brand', err)
        } finally {
            setIsDeleting(false)
            setDeleting(null)
        }
    }

    useEffect(() => {
        if (isDeleting && networkPending) {
            deleteReachedNetworkPending.current = true
            return
        }

        if (isDeleting && !networkPending && (deleteReachedNetworkPending.current || brandNetworkError)) {
            deleteReachedNetworkPending.current = false
            setDeleting(null)
            setIsDeleting(false)
        }
    }, [isDeleting, networkPending, brandNetworkError])

    if (!brands.length) {
        return <BrandsLandingPage onClickAddNew={onClickAddNew} />
    }

    return <ListContainer onClickAddNew={onClickAddNew} addButtonText={getValue('sms_enrollment_add_brand_button')}>
        <Table
            columns={[
                getValue('sms_enrollment_brand_id_column_name'),
                getValue('sms_enrollment_brand_column_name'),
                getValue('sms_enrollment_registered_column_name'),
                getValue('sms_enrollment_status_column_name'),
                ''
            ]}
            rows={rows}
            data-testid='sms-tabs-list-table'
        />
        <ConfirmDeleteModal
            isOpen={deleting != null || isDeleting}
            isLoading={isDeleting}
            type={'brand'}
            selectedItem={deleting}
            onClose={() => {
                deleteReachedNetworkPending.current = false
                setDeleting(null)
                setIsDeleting(false)
            }}
            onDelete={onDeleteHandler}
        />
    </ListContainer>
}

export default Enrollment
